import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { TourStep, TourTarget } from './tours';
import '../../styles/product-tour.css';

interface ProductTourProps {
    steps: TourStep[];
    open: boolean;
    /** `shown` is false when no step could be displayed (nothing on screen to point at). */
    onClose: (completed: boolean, shown?: boolean) => void;
}

interface Rect { top: number; left: number; width: number; height: number; }

const PAD = 6;          // spotlight padding around the target
const GAP = 14;         // distance between spotlight and card
const CARD_W = 300;

const isVisible = (el: Element) => {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return false;
    const cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
};

/** Resolve a step target to the first *visible* element (hidden forms stay mounted in some modules). */
const resolveTarget = (target?: TourTarget): HTMLElement | null => {
    if (!target) return null;
    if (typeof target === 'string') {
        const all = Array.from(document.querySelectorAll<HTMLElement>(target));
        return all.find(isVisible) || null;
    }
    const scope = target.within ? document.querySelector(target.within) : document.body;
    if (!scope) return null;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
        if ((node.textContent || '').trim() === target.text) {
            const host = node.parentElement;
            const el = (target.closest ? host?.closest<HTMLElement>(target.closest) : host) || host;
            if (el && isVisible(el)) return el;
        }
        node = walker.nextNode();
    }
    return null;
};

const waitForTarget = (target: TourTarget | undefined, timeoutMs: number): Promise<HTMLElement | null> =>
    new Promise((resolve) => {
        if (!target) return resolve(null);
        const started = Date.now();
        const tick = () => {
            const el = resolveTarget(target);
            if (el || Date.now() - started > timeoutMs) return resolve(el);
            window.setTimeout(tick, 120);
        };
        tick();
    });

/** Index of the first step that can be shown (an intro card always can), or null on timeout. */
const waitForFirstAvailable = (steps: TourStep[], timeoutMs: number): Promise<number | null> =>
    new Promise((resolve) => {
        const started = Date.now();
        const tick = () => {
            const i = steps.findIndex((s) => !s.target || resolveTarget(s.target));
            if (i !== -1) return resolve(i);
            if (Date.now() - started > timeoutMs) return resolve(null);
            window.setTimeout(tick, 120);
        };
        tick();
    });

type Side = 'top' | 'bottom' | 'left' | 'right';

const placeCard = (rect: Rect | null, cardH: number, preferred?: Side) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(CARD_W, vw - 24);
    if (!rect) {
        return { top: Math.max(12, (vh - cardH) / 2), left: (vw - w) / 2, side: null as Side | null, w };
    }
    const space: Record<Side, number> = {
        bottom: vh - (rect.top + rect.height),
        top: rect.top,
        right: vw - (rect.left + rect.width),
        left: rect.left,
    };
    const fits = (s: Side) =>
        s === 'bottom' || s === 'top' ? space[s] > cardH + GAP + 8 : space[s] > w + GAP + 8;
    const order: Side[] = [preferred, 'bottom', 'right', 'top', 'left'].filter(Boolean) as Side[];
    const side = order.find(fits) || 'bottom';
    let top: number;
    let left: number;
    if (side === 'bottom' || side === 'top') {
        top = side === 'bottom' ? rect.top + rect.height + GAP : rect.top - cardH - GAP;
        left = rect.left + rect.width / 2 - w / 2;
    } else {
        left = side === 'right' ? rect.left + rect.width + GAP : rect.left - w - GAP;
        top = rect.top + rect.height / 2 - cardH / 2;
    }
    left = Math.max(12, Math.min(left, vw - w - 12));
    top = Math.max(12, Math.min(top, vh - cardH - 12));
    return { top, left, side, w };
};

const ProductTour: React.FC<ProductTourProps> = ({ steps, open, onClose }) => {
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);
    const [ready, setReady] = useState(false);
    const [cardH, setCardH] = useState(170);
    const targetRef = useRef<HTMLElement | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const dirRef = useRef<1 | -1>(1);
    const startedRef = useRef(false);
    const [retry, setRetry] = useState(0);
    // Glide between steps only after the first step has painted in place
    const [animate, setAnimate] = useState(false);

    const step = steps[index];
    const isLast = index === steps.length - 1;

    const measure = useCallback(() => {
        const el = targetRef.current;
        if (!el) { setRect(null); return; }
        const r = el.getBoundingClientRect();
        setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    }, []);

    // Resolve the current step's target; steps whose target never appears are skipped.
    useEffect(() => {
        if (!open || !step) return;
        let cancelled = false;
        setReady(false);
        if (!startedRef.current) {
            // Opening: the page may still be loading, and some steps only exist in certain views
            // (e.g. a category page vs the category list). Start at the first step that shows up.
            waitForFirstAvailable(steps, 8000).then((first) => {
                if (cancelled) return;
                startedRef.current = true;
                if (first === null) onClose(false, false);
                else if (first !== index) setIndex(first);
                else setRetry((n) => n + 1);
            });
            return () => { cancelled = true; };
        }
        waitForTarget(step.target, step.target ? 1500 : 0).then((el) => {
            if (cancelled) return;
            if (step.target && !el) {
                const next = index + dirRef.current;
                if (next >= 0 && next < steps.length) setIndex(next);
                else onClose(dirRef.current === 1);
                return;
            }
            targetRef.current = el;
            if (el) {
                const r = el.getBoundingClientRect();
                if (r.top < 70 || r.bottom > window.innerHeight - 20) {
                    el.scrollIntoView({ block: 'center', behavior: 'auto' });
                }
            }
            measure();
            setReady(true);
        });
        return () => { cancelled = true; };
    }, [open, index, step, steps, measure, onClose, retry]);

    // Follow the target while scrolling / resizing
    useEffect(() => {
        if (!open) return;
        const onMove = () => measure();
        window.addEventListener('resize', onMove);
        window.addEventListener('scroll', onMove, true);
        const id = window.setInterval(onMove, 400); // catches layout shifts (e.g. data arriving)
        return () => {
            window.removeEventListener('resize', onMove);
            window.removeEventListener('scroll', onMove, true);
            window.clearInterval(id);
        };
    }, [open, measure]);

    useLayoutEffect(() => {
        if (cardRef.current) setCardH(cardRef.current.offsetHeight);
    }, [index, ready]);

    useEffect(() => {
        if (!ready || animate) return;
        const id = window.requestAnimationFrame(() => setAnimate(true));
        return () => window.cancelAnimationFrame(id);
    }, [ready, animate]);

    const go = useCallback((delta: 1 | -1) => {
        dirRef.current = delta;
        const next = index + delta;
        if (next < 0) return;
        if (next >= steps.length) { onClose(true); return; }
        setIndex(next);
    }, [index, steps.length, onClose]);

    // Keyboard: ← → Enter Esc
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(false); }
            else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); go(1); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, go, onClose]);

    useEffect(() => {
        if (open && ready) cardRef.current?.focus({ preventScroll: true });
    }, [open, ready, index]);

    if (!open || !step) return null;

    const pos = placeCard(step.target ? rect : null, cardH, step.placement);

    return createPortal(
        <div className="ax-tour-root" role="presentation" data-animate={animate ? 'true' : undefined}>
            {/* Click shield: the page underneath is paused while the tour runs */}
            <div className="ax-tour-shield" aria-hidden="true" />
            {rect && step.target ? (
                <div
                    className="ax-tour-spot"
                    aria-hidden="true"
                    style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
                >
                    <span className="ax-tour-spot__ring" />
                </div>
            ) : (
                <div className="ax-tour-dim" aria-hidden="true" />
            )}

            <div
                ref={cardRef}
                className={`ax-tour-card${ready ? ' is-ready' : ''}${pos.side ? ` is-${pos.side}` : ' is-center'}`}
                style={{ top: pos.top, left: pos.left, width: pos.w }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="ax-tour-title"
                aria-describedby="ax-tour-body"
                tabIndex={-1}
            >
                <div className="ax-tour-progress" aria-hidden="true">
                    {steps.map((s, i) => (
                        <span key={s.title} className={i <= index ? 'is-on' : ''} />
                    ))}
                </div>
                <div className="ax-tour-head">
                    <span className="ax-tour-count">{index + 1}<i>/{steps.length}</i></span>
                    <button type="button" className="ax-tour-x" onClick={() => onClose(false)} aria-label="Close tour">
                        <X size={14} />
                    </button>
                </div>
                <h2 id="ax-tour-title" className="ax-tour-title">{step.title}</h2>
                <p id="ax-tour-body" className="ax-tour-body">{step.body}</p>
                <div className="ax-tour-foot">
                    {index === 0 ? (
                        <button type="button" className="ax-tour-skip" onClick={() => onClose(false)}>Skip tour</button>
                    ) : (
                        <button type="button" className="ax-tour-back" onClick={() => go(-1)} aria-label="Previous step">
                            <ArrowLeft size={14} />
                        </button>
                    )}
                    <button type="button" className="ax-tour-next" onClick={() => go(1)}>
                        {isLast ? <>Got it <Check size={14} /></> : index === 0 && !step.target ? <>Show me <ArrowRight size={14} /></> : <>Next <ArrowRight size={14} /></>}
                    </button>
                </div>
            </div>
            <span className="ax-tour-sr" aria-live="polite">{`Step ${index + 1} of ${steps.length}: ${step.title}`}</span>
        </div>,
        document.body
    );
};

export default ProductTour;
